import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { userApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { authFormSchema, type AuthFormData } from '@/lib/validationSchemas';
import { toast } from 'sonner';

export default function AuthForm() {
  // Form state with validation
  const form = useForm<AuthFormData>({
    resolver: zodResolver(authFormSchema),
    mode: 'onChange',
    defaultValues: {
      email: ''
    }
  });
  
  const { control, handleSubmit, setValue } = form;

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { signInWithEmail, enterGuestMode, signOut } = useAuth();

  // Check for URL hash errors on component mount
  useEffect(() => {
    const checkUrlErrors = () => {
      const hash = window.location.hash;
      if (hash.includes('error=access_denied')) {
        const urlParams = new URLSearchParams(hash.substring(1));
        const error = urlParams.get('error');
        const errorCode = urlParams.get('error_code');
        const errorDescription = urlParams.get('error_description');

        let errorMessage = '';
        if (errorCode === 'otp_expired') {
          errorMessage = 'Your magic link has expired. Magic links are only valid for a limited time for security. Please enter your email below to get a fresh link.';
        } else if (error === 'access_denied') {
          errorMessage = 'Access denied. Please try signing in again with a new magic link.';
        } else {
          errorMessage = errorDescription ? decodeURIComponent(errorDescription) : 'Authentication failed. Please try again.';
        }

        setMessage(`Error: ${errorMessage}`);

        // Also show a toast for immediate feedback
        if (errorCode === 'otp_expired') {
          toast.error('Magic link expired', {
            description: 'Please request a new magic link below.',
            duration: 5000,
          });
        } else {
          toast.error('Authentication failed', {
            description: 'Please try signing in again.',
            duration: 4000,
          });
        }

        // Clean up the URL hash
        window.history.replaceState(null, '', window.location.pathname);
      }
    };

    checkUrlErrors();
  }, []);

  // Mutation for creating guest user with retries
  const createGuestUserMutation = useMutation({
    mutationFn: userApi.createUser,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    onSuccess: () => {
      // User created successfully - navigate to home page
      setMessage('');
      navigate('/');
    },
    onError: (error) => {
      // Creation failed - exit guest mode and show error
      signOut(); // This will clear guest mode
      setMessage(`Failed to create guest account: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  const onSubmit = async (data: AuthFormData) => {
    setLoading(true);
    setMessage('');

    const { error } = await signInWithEmail(data.email);

    if (error) {
      setMessage(`Error: ${error.message}`);
      toast.error('Failed to send magic link', {
        description: error.message,
        duration: 4000,
      });
    } else {
      setMessage('Magic link sent! Check your email and click the link to sign in. The link will expire in 1 hour for security.');
      setValue('email', '');
      toast.success('Magic link sent!', {
        description: 'Check your email and click the link to sign in.',
        duration: 4000,
      });
    }

    setLoading(false);
  };

  const handleGuestMode = () => {
    setMessage('');

    // Enter guest mode immediately (optimistic)
    enterGuestMode();

    // Then create the user on the server in the background
    createGuestUserMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-card border-border shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <motion.h1
                  className="text-4xl font-bold tracking-tight mb-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  Altitude
                </motion.h1>
                <motion.p
                  className="text-muted-foreground"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  Reach new heights in your fitness journey
                </motion.p>
              </div>

              <Form {...form}>
                <motion.form
                  onSubmit={handleSubmit(onSubmit)}
                  className="space-y-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <FormField
                    control={control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          Email address 
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder="Enter your email"
                            disabled={loading}
                            className="h-11"
                          />
                        </FormControl>
                        <FormDescription>
                          A magic link will be sent to your email
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />


                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11"
                  >
                    {loading ? 'Sending...' : 'Send Magic Link'}
                  </Button>
                </motion.form>
              </Form>

              <div className="my-6">
                <div className="relative">
                  <Separator />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="bg-card px-3 text-sm text-muted-foreground">or</span>
                  </div>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <motion.div whileHover={{ y: -1 }} transition={{ duration: 0.2 }}>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGuestMode}
                    className="w-full h-11"
                    disabled={createGuestUserMutation.isPending}
                  >
                    {createGuestUserMutation.isPending ? 'Setting up guest account...' : 'Continue as Guest'}
                  </Button>
                </motion.div>

                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-center">
                    Guest Mode: Start tracking immediately
                  </p>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>• Your data is stored securely on our servers</p>
                    <p>• No email required to get started</p>
                    <p>• Create an account anytime to unlock cloud features</p>
                  </div>
                </div>
              </motion.div>

              {message && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Alert className={`mt-6 ${message.includes('Error')
                      ? 'border-destructive/20 bg-destructive/10'
                      : 'border-green-500/20 bg-green-500/10'
                    }`}>
                    <AlertDescription className={
                      message.includes('Error')
                        ? 'text-destructive'
                        : 'text-green-700 dark:text-green-400'
                    }>
                      {message}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}

              <motion.div
                className="mt-6 text-center space-y-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <p className="text-sm text-muted-foreground">
                  A magic link will be sent to your email
                </p>
                <p className="text-sm text-muted-foreground">
                  Click it to sign in securely without a password
                </p>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}